import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Pages
import ClientPortal from './pages/ClientPortal';
import AdminDashboard from './pages/AdminDashboard';
import DrafterPortal from './pages/DrafterPortal';
import Login from './pages/Login';
import PaymentComplete from './pages/PaymentComplete';
import AcceptInvite from './pages/AcceptInvite';

// Components
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import CompanySettings from './components/CompanySettings';
import TeamManagementModal from './components/TeamManagementModal';

// Context
import { AuthProvider, useAuth } from './hooks/useAuth';
import { TenantProvider } from './hooks/useTenant';

// Styles
import './styles/globals.css';

function AppContent() {
  const { user, role, isLoading, currentTenant } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showCompanySettings, setShowCompanySettings] = useState(false);
  const [showTeamManagement, setShowTeamManagement] = useState(false);
  const [currentCompany, setCurrentCompany] = useState(null);

  // Fetch current company for settings modal
  const fetchCurrentCompany = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/companies/current', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentCompany(data);
      }
    } catch (err) {
      console.error('Failed to fetch company:', err);
    }
  };

  const handleOpenSettings = () => {
    fetchCurrentCompany();
    setShowCompanySettings(true);
  };

  const handleCompanyUpdate = (updatedCompany) => {
    setCurrentCompany(updatedCompany);
    // Reload to apply new branding
    window.location.reload();
  };

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loader"></div>
        <p>Loading ShopDrawings.AI...</p>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  return (
    <div className="app-container">
      <Navbar
        onMenuClick={() => setSidebarOpen(!sidebarOpen)}
        onSettingsClick={handleOpenSettings}
        onTeamClick={() => setShowTeamManagement(true)}
      />

      {/* Company Settings Modal */}
      {showCompanySettings && currentCompany && (
        <CompanySettings
          company={currentCompany}
          onClose={() => setShowCompanySettings(false)}
          onUpdate={handleCompanyUpdate}
        />
      )}

      {/* Team Management Modal */}
      {showTeamManagement && (
        <TeamManagementModal onClose={() => setShowTeamManagement(false)} />
      )}
      <div className="main-content">
        <Sidebar isOpen={sidebarOpen} userRole={role || 'client'} />
        <div className={`page-content ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
          <Routes>
            {/* Client Routes */}
            <Route path="/client" element={<ClientPortal />} />
            <Route path="/client/projects" element={<ClientPortal tab="projects" />} />
            <Route path="/client/upload" element={<ClientPortal tab="upload" />} />
            <Route path="/client/invoices" element={<ClientPortal tab="invoices" />} />
            <Route path="/client/messages" element={<ClientPortal tab="messages" />} />

            {/* Admin Routes */}
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/projects" element={<AdminDashboard tab="projects" />} />
            <Route path="/admin/clients" element={<AdminDashboard tab="clients" />} />
            <Route path="/admin/team" element={<AdminDashboard tab="team" />} />
            <Route path="/admin/financials" element={<AdminDashboard tab="financials" />} />
            <Route path="/admin/standards" element={<AdminDashboard tab="standards" />} />
            <Route path="/admin/rewards" element={<AdminDashboard tab="rewards" />} />
            <Route path="/admin/ai" element={<AdminDashboard tab="ai" />} />

            {/* Drafter Routes */}
            <Route path="/drafter" element={<DrafterPortal />} />
            <Route path="/drafter/assignments" element={<DrafterPortal tab="assignments" />} />
            <Route path="/drafter/time" element={<DrafterPortal tab="time" />} />
            <Route path="/drafter/standards" element={<DrafterPortal tab="standards" />} />
            <Route path="/drafter/rewards" element={<DrafterPortal tab="rewards" />} />

            {/* Settings Routes */}
            <Route path="/settings/branding" element={<AdminDashboard tab="branding" />} />
            <Route path="/settings/billing" element={<AdminDashboard tab="billing" />} />
            <Route path="/settings/users" element={<AdminDashboard tab="users" />} />
            <Route path="/settings/integrations" element={<AdminDashboard tab="integrations" />} />

            {/* Default redirect based on role */}
            <Route path="/" element={
              (role === 'admin' || role === 'owner') ? <Navigate to="/admin" /> :
              role === 'drafter' ? <Navigate to="/drafter" /> :
              <Navigate to="/client" />
            } />

            {/* Catch-all: an unmatched path must never render a blank page */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <TenantProvider>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Payment complete page - accessible without full auth */}
            <Route path="/payment/complete" element={<PaymentComplete />} />
            {/* Team invitation acceptance page */}
            <Route path="/invite/:token" element={<AcceptInvite />} />
            {/* All other routes */}
            <Route path="/*" element={<AppContent />} />
          </Routes>
        </Router>
      </AuthProvider>
    </TenantProvider>
  );
}

export default App;
